// systemservicesSlice.ts
import { createAction, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { createAsyncThunk } from "@reduxjs/toolkit";
import { addToQueue } from '@rhino/shared/services/redux/prefsSlice';
import { fnDictSetIndex } from '@rhino/shared/services/shared';
import { OrbotnikColourVariant } from '@rhino/shared/types/types';
let messageIdCounter = 0;

function generateUniqueID() {
    return messageIdCounter++
}

const availableColors: OrbotnikColourVariant[] = ['geekblue','magenta', 'lime', 'volcano', 'gold', 'red', 'orange', 'green', 'cyan', 'blue',  'purple'];
type LogTypeColorMapping = {
    [key: string]: OrbotnikColourVariant;
  };
let colorCounter = 0;
const logTypeColors: LogTypeColorMapping = {};

export interface ServiceSettings {
  name: string;
  description: string;
  servicetype: ServiceType;
  monitoring?: {
    refresh: number;
    logfile: LogFile;
  }
  settings?: any
  remote?: Remote;
  crontab?: CronTab;
}

interface Remote {
  name: string;
  user: string;
}

interface LogFile {
  path: string;
  initialtail: number;
  monitor: number;
  syslogregex?: string;
}

interface CronTab {
  commentid: string;
  user: string;
  psmonitor: PsMonitor;
}

interface PsMonitor {
  cmdlinelike: string;
}

interface CommonServiceAttributes {
  [key: string]: any
  active?: boolean;
  stateicon?: string;
  statedesc?: string;
  enabled?: boolean;
}

interface ServiceStatus extends CommonServiceAttributes {
  systemd?: CommonServiceAttributes;
  watcher?: CommonServiceAttributes;
  crontab?: CommonServiceAttributes;
  servicelog?: ServiceLog;
  servername?: string;
  lastactive?: number;
  nextscheduled?: number;
}

interface ServiceLog {
  [key: string]: LogHistoryEvent[] | LogEvent | string | { [key: string]: boolean } | undefined;
  keys?: string;
  history?: LogHistoryEvent[];
  encounteredEmptyLines?: {
    [key: string]: boolean;
  };
}

export interface LogEvent {
logType?: string
lines?: string;
date?: string;
}

interface ParsedLine {
line: string;
ts?: any;
}
export interface ParsedLogEvent {
  logType?: string;
  lines?: ParsedLine;
  date?: number;
  color?: OrbotnikColourVariant;
}

export interface LogHistoryEvent {
  id:number;
  logType: string;
  lines: string;
  date: number;
  color: OrbotnikColourVariant;
}
  
interface ServicesSettings {
[serviceName: string]: ServiceSettings;
}
interface ServicesStatuses {
[serviceName: string]: ServiceStatus;
}
interface SystemServiceState {
  status: ServicesStatuses;
  settings: ServicesSettings;
  addedServices: string[];
}

type ServiceType = 'systemd' | 'crontab';


export const handleLogKeyRefCallback = createAsyncThunk(
  'systemservices/handleLogKeyRefCallback',
  async (payload: { serviceName: string, logKey: string }, thunkAPI) => {
    const state = thunkAPI.getState() as { systemservices: SystemServiceState };

    function convertToParsedEvent(logKey:string, log?: LogEvent): ParsedLogEvent[] | undefined {
      if (!log) return;
      let sourceLines = log.lines
      if (!sourceLines) return;
      let timestamp = log.date
      let ts:number
  
      // If it's a string, parse it as a number
      if (typeof timestamp === "string") {
        ts = parseFloat(timestamp);
      } else if (typeof timestamp === "number") {
        ts = timestamp
      } else {
        ts = new Date().getTime()
      }

      const logLines = sourceLines.trim().split("\n");

      return logLines.map(line => {
          try {
              const parsedLine = JSON.parse(line);
              return {
                  logType: logKey,
                  lines: {
                      line: parsedLine.line,
                      ts: parsedLine.ts
                  },
                  date: ts
              };
          } catch (error) {
              return {
                logType: logKey,
                lines: {line:line},
                date: ts}
          }
      }).filter(Boolean) as ParsedLogEvent[]; // filter out any null values

    }

  
    const parsedLogEvents = convertToParsedEvent(payload.logKey,state.systemservices?.status?.[payload.serviceName]?.servicelog?.[payload.logKey] as LogEvent | undefined)
    if (!parsedLogEvents) return;
    parsedLogEvents.forEach(logEvent => {
      try {
          thunkAPI.dispatch(addServiceLogEvent({ serviceName: payload.serviceName, logEvent }));
      } catch (oError) {
          console.log(oError);
      }
    });

    
    return payload; // This isn't really necessary unless you want to use it in the fulfilled/rejected actions.
  }
);


export const updateServiceSettingAsync = createAsyncThunk(
  "systemservices/updateServiceSettings",
  async (payload: { serviceName: string, keyaddress: string; keyvalue: any; }, thunkAPI) => {
    const { dispatch,getState } = thunkAPI;
    let newKeyAddress = '';
    dispatch(updateServiceSetting({serviceName: payload.serviceName, keyaddress: payload.keyaddress, keyvalue: payload.keyvalue }));
    newKeyAddress = `services.${payload.serviceName}.${payload.keyaddress}`;
    
    console.log('send update to server',`keyaddress: ${newKeyAddress}`,`keyvalue: ${payload.keyvalue}`)
    dispatch(addToQueue({
      type: 4,
      prefname: 'vivo',
      keyaddress: newKeyAddress,
      keyvalue: payload.keyvalue
    }));


    return payload; // Return the payload
  }
);

export const updateServiceStatusAsync = createAsyncThunk(
  "auth/updateServiceStatus",
  async (payload: { objectName: string, keyaddress: string; keyvalue: any; }, thunkAPI) => {
    // const { dispatch } = thunkAPI;

    // dispatch(updateObjectsStatus(payload));

    return payload; // Return the payload
  }
);




const initialState: SystemServiceState = {
  status: {},
  settings: {},
  addedServices: [],
};
const systemservicesSlice = createSlice({
  name: 'systemservices',
  initialState,
  reducers: {
    setServicesStatuses: (state, action: PayloadAction<any>) => {
      // replace the entire state
      console.log(action.payload)
    //   state.status = action.payload
    },
    updateServicesStatus: (state, action: PayloadAction<{ serviceName: string, keyaddress: string, keyvalue: any }>) => {
      // console.log(action.payload)
      const { serviceName, keyaddress, keyvalue } = action.payload;
      // console.log(state.settings)
      if (state.settings?.[serviceName]) {
        if (keyaddress.startsWith('state.')) {
          fnDictSetIndex(state, `status.${serviceName}.${keyaddress.substring(6)}`, keyvalue);}
        else {
          // console.log('invalid key address sent',keyaddress)
          fnDictSetIndex(state, `settings.${serviceName}.${keyaddress}`, keyvalue);
        }
        // console.log(fnDictGetIndex(state,''))
      }
    },
    setServicesSettings: (state, action: PayloadAction<any>) => {
        console.log(action.payload)
        state.settings = action.payload;
    },
    updateServiceSetting: (state, action: PayloadAction<{ serviceName: string, keyaddress: string, keyvalue: any }>) => {
        const { serviceName, keyaddress, keyvalue } = action.payload;
        console.log(action.payload)
        fnDictSetIndex(state, `settings.${serviceName}.${keyaddress}`, keyvalue);
    },    
    addServiceLogEvent: (state, action: PayloadAction<{ serviceName: string, logEvent: ParsedLogEvent }>) => {
      const { serviceName, logEvent } = action.payload;
      const serviceStatus = state.status[serviceName];
      
      if (serviceStatus && logEvent) {
          // COLOR ASSIGNMENT LOGIC
          // If the log type has no color yet, assign the next color
          let logType = logEvent.logType || 'NA';
          if (!logTypeColors[logType]) {
              logTypeColors[logType] = availableColors[colorCounter];
              colorCounter = (colorCounter + 1) % availableColors.length; // Move to the next color and wrap around if necessary
          }
  
          logEvent.color = logTypeColors[logType]; // Assign the color to the logEvent
    
          if (!serviceStatus?.servicelog) {
              serviceStatus.servicelog = {};
          }
          if (logEvent.lines?.line == "--EMPTY--") {
            // Ensure the encounteredEmptyLines object exists within servicelog
            if (!serviceStatus.servicelog.encounteredEmptyLines) {
                serviceStatus.servicelog.encounteredEmptyLines = {};
            }

            // Use 'none' as a default value if logType doesn't exist
            const logType = logEvent.logType || 'none';
            
            // If this logType hasn't encountered --EMPTY-- before, mark it as encountered
            if (!serviceStatus.servicelog.encounteredEmptyLines[logType]) {
                serviceStatus.servicelog.encounteredEmptyLines[logType] = true;
                logEvent.lines.line = `No data found in ${logType} log file`
            } else {
              return
            }

          }
          
          const HistoryLine = () => ({
            id: generateUniqueID(),
            logType: logType,
            lines: logEvent.lines?.line || '',
            date: logEvent.lines?.ts || logEvent.date || new Date().getTime(),
            color: logEvent.color || 'geekblue',
        });
        
          if (!serviceStatus.servicelog?.history) {
            serviceStatus.servicelog.history = [HistoryLine()];
          } else {
            const lastLogEvent = serviceStatus.servicelog.history[serviceStatus.servicelog.history.length - 1];
            
            // Check if the current logEvent matches the last logEvent in history
            if (logEvent.lines && 
                  lastLogEvent.lines && 
                  lastLogEvent.logType === logEvent.logType &&
                  (logEvent.lines?.ts ? 
                    Math.abs(Math.floor(lastLogEvent.date) - Math.floor(logEvent.lines?.ts)) < 2
                    : lastLogEvent.date === logEvent.date 
                  )
                ) {
                  // // console.log('SO',`${lastLogEvent.logType} &&
                  // // ${lastLogEvent.date} === ${logEvent.date} &&
                  // // ${Math.floor(lastLogEvent.lines?.ts)-Math.floor(logEvent.lines?.ts)}
                  // ${(logEvent.date ? 
                  //   'lastLogEvent.date === logEvent.date '
                  //   : 'Math.abs(Math.floor(lastLogEvent.lines?.ts) - Math.floor(logEvent.lines?.ts)) < 2'
                  // )}`)
                // Append lines.line to the last logEvent in history
                lastLogEvent.lines += `\n${logEvent.lines.line}`;
            } else {
                // console.log('NOT',`${lastLogEvent.logType} === ${logEvent.logType} &&
                //   ${lastLogEvent.date} === ${logEvent.date} &&
                //   ${Math.floor(lastLogEvent.lines?.ts)-Math.floor(logEvent.lines?.ts)}}`)
                serviceStatus.servicelog.history.push(HistoryLine());
                
                // Ensure only 100 items are retained
                while (serviceStatus.servicelog.history.length > 100) {
                    serviceStatus.servicelog.history.shift(); // Remove the oldest item
                }
            }
          }          
          
      }
    },     
    addVivoWatchService: (state, action) => {
      if (!state.addedServices.includes(action.payload)) {
        state.addedServices.push(action.payload);
      }
    },
    removeVivoWatchService: (state, action) => {
      state.addedServices = state.addedServices.filter(service => service !== action.payload);
    }    
  },
});
 
export const { setServicesStatuses, updateServicesStatus, setServicesSettings, updateServiceSetting, addServiceLogEvent,addVivoWatchService,removeVivoWatchService } = systemservicesSlice.actions;
export const selectAddedVivoWatchServices = (state: { systemservices: SystemServiceState }) => state.systemservices.addedServices;
export const selectStatuses = (state: { systemservices: SystemServiceState }) => state.systemservices.status;
export const selectSettings = (state: { systemservices: SystemServiceState }) => state.systemservices.settings;
// export const selectsystemservicesServiceSettings = (state: { systemservices: systemservicesObject }) => state.systemservices.settings;

export const selectServiceStatusByName = (state: { systemservices: SystemServiceState }, serviceName: string) => state.systemservices?.status?.[serviceName];
// export const selectServiceStatusByName_LogHx = (state: { systemservices: SystemServiceState }, serviceName: string) => state.systemservices?.status?.[serviceName].servicelog?.history;

export const selectServiceStatusByName_LogHx = (state: { systemservices: SystemServiceState }, serviceName: string) => {
  let history=null
  try {
    history = state.systemservices?.status?.[serviceName].servicelog?.history;
  } catch {

  }
  return history;
};

export const selectServiceSettingsByName = (state: { systemservices: SystemServiceState }, serviceName: string) => state.systemservices?.settings?.[serviceName];
export const selectVivoWatchingKey = (state: { systemservices: SystemServiceState }, sputnikKey: string) => 
    state.systemservices.addedServices?.includes(sputnikKey) ?? false;

// In your slice or actions file
export const triggerLogKeyRefCallbacks = createAction<{ serviceName: string, data: any }>('systemservices/triggerLogKeyRefCallbacks');

export default systemservicesSlice.reducer;